/**
 * Column descriptions for the per-day daily condition table.
 *
 * Sources: docs/collaboration/backend/01-training-load-data-contract.md §2 (device.*),
 * the daily_condition prompt glossary (states, z-scores, thresholds) and the backend's
 * description of the EPG daily sync (custom.*). Keys are the dotted table keys.
 */

export interface ColumnDoc {
  description: string;
  /** Who writes it and when — the main reason two similar columns disagree */
  updated?: string;
}

const DEVICE_SYNC = '러닝 기록 동기화 때만 (백엔드 TrainingLoadService)';
const DAILY_SYNC = '매일 새벽 정기 동기화 (EPG 03:30)';
const SIGNUP = '가입·프로필 변경 시';
const RESULT = 'AI 판정 결과';

export const GROUP_DOCS: Record<string, string> = {
  user: '사용자 기본 정보 (users.user_info). language 가 AI 출력 언어를 정한다.',
  device:
    '기기 데이터 블록 (users.device). 러닝을 동기화할 때 백엔드가 채운다 — 쉬는 날에는 값이 마지막 러닝 시점에 멈춰 있다.',
  custom: 'EPG 일일 동기화 블록 (users.custom). 매일 새벽 다시 계산해 보낸다. IPG 에는 없다.',
  result: '모델이 반환한 판정. 판정 근거는 이 행의 user / device / custom 이 전부다 (request 는 비어 있음).',
};

export const COLUMN_DOCS: Record<string, ColumnDoc> = {
  // ── user ──
  'user.name': { description: '닉네임', updated: SIGNUP },
  'user.language': {
    description: 'AI 출력 언어 (IETF 코드, 예: et·ko·en). message/reason/guide 가 이 언어로 나온다.',
    updated: SIGNUP,
  },
  'user.gender': { description: '성별', updated: SIGNUP },
  'user.birth_date': { description: '생년월일', updated: SIGNUP },

  // ── device ──
  'device.platform': { description: '앱 플랫폼 (android / ios)', updated: '앱 (갱신 시점 미확인)' },
  'device.timezone': {
    description: '기기 시간대. 채팅 프롬프트에서는 빼지만 일일 판정에는 그대로 들어간다.',
    updated: '앱 (갱신 시점 미확인)',
  },
  'device.device_source': {
    description:
      '데이터를 준 기기/서비스 (ios_healthkit, android_health_connect, garmin_connect 등). AI 가 이걸로 플랫폼을 추론한다.',
    updated: '기기 연동·설정 변경 시',
  },
  'device.load.atl': {
    description: 'ATL — 7일 EWMA "피로". 마지막 러닝 시점 값.',
    updated: `${DEVICE_SYNC} · 쉬는 날 감쇠 미반영`,
  },
  'device.load.ctl': {
    description: 'CTL — 42일 EWMA "체력". 마지막 러닝 시점 값.',
    updated: `${DEVICE_SYNC} · 쉬는 날 감쇠 미반영`,
  },
  'device.load.tsb': {
    description:
      'TSB = CTL − ATL "컨디션"(+ 회복, − 피로). 마지막 러닝 시점 값이라 custom.training_load.tsb 와 크게 다를 수 있다. 현재 모델은 주로 이 값을 근거로 인용한다.',
    updated: `${DEVICE_SYNC} · 쉬는 날 감쇠 미반영`,
  },
  'device.load.acwr': {
    description:
      'ACWR — 최근 7일 TRIMP 합 ÷ 28일 주평균. 부상 위험 지표 (0.8–1.3 안전, ≥1.5 위험). Chronic=0 이면 null. 마지막 러닝 시점 값.',
    updated: `${DEVICE_SYNC} · 쉬는 날 감쇠 미반영`,
  },
  'device.latest_session.date': { description: '마지막 러닝 세션 날짜', updated: DEVICE_SYNC },
  'device.latest_session.distance_km': { description: '마지막 세션 거리 (km)', updated: DEVICE_SYNC },
  'device.latest_session.duration_min': { description: '마지막 세션 시간 (분)', updated: DEVICE_SYNC },
  'device.latest_session.avg_pace': { description: '마지막 세션 평균 페이스 (분:초/km)', updated: DEVICE_SYNC },
  'device.latest_session.avg_hr': { description: '마지막 세션 평균 심박 (bpm)', updated: DEVICE_SYNC },
  'device.latest_session.max_hr': { description: '마지막 세션 최대 심박 (bpm)', updated: DEVICE_SYNC },
  'device.latest_session.trimp': {
    description: '마지막 세션 TRIMP (시간 × 심박 강도로 낸 훈련 부하)',
    updated: DEVICE_SYNC,
  },
  'device.latest_session.cadence_avg': {
    description: '평균 케이던스 (spm). 기기에 따라 null.',
    updated: DEVICE_SYNC,
  },
  'device.latest_session.power_avg_w': { description: '평균 파워 (W). 대부분 null.', updated: DEVICE_SYNC },
  'device.latest_session.zone_times_sec': {
    description: '심박 존 1–5 별 누적 시간 (초)',
    updated: DEVICE_SYNC,
  },
  'device.latest_session.running_dynamics': {
    description: '러닝 다이내믹스 (지면 접촉 시간 등). 현재 미제공 — 대부분 null.',
    updated: DEVICE_SYNC,
  },
  'device.weekly.trimp': { description: '이번 주(월–일) TRIMP 합계. 월요일 0시 리셋.', updated: DEVICE_SYNC },
  'device.weekly.sessions': { description: '이번 주 세션 수', updated: DEVICE_SYNC },
  'device.weekly.distance_km': { description: '이번 주 총 거리 (km)', updated: DEVICE_SYNC },
  'device.trend7d': {
    description: '최근 7일 일별 TRIMP 배열 ({date, trimp}, 훈련 없는 날 0). 마지막 동기화 기준 7일.',
    updated: DEVICE_SYNC,
  },
  'device.last4_weeks.avg_weekly_trimp': { description: '직전 28일 평균 주간 TRIMP', updated: DEVICE_SYNC },
  'device.last4_weeks.avg_weekly_sessions': { description: '직전 28일 평균 주간 세션 수', updated: DEVICE_SYNC },
  'device.last4_weeks.avg_weekly_distance_km': {
    description: '직전 28일 평균 주간 거리 (km)',
    updated: DEVICE_SYNC,
  },
  'device.hr.max_hr': { description: '최대 심박 (bpm). 측정값 또는 220 − 나이 추정.', updated: SIGNUP },
  'device.hr.resting_hr': {
    description: '안정시 심박 {value, source}. TRIMP 계산 입력. source=profile_default 면 실측이 아닌 기본값.',
    updated: DEVICE_SYNC,
  },
  'device.vdot.value': {
    description: '피트니스 추정치 (VO2Max, ml/kg/min). 기기 제공값 그대로. 30–85 밖이면 프롬프트가 무시하라고 지시.',
    updated: '기기 신규 측정 시',
  },
  'device.vdot.source': { description: 'VDOT 출처 (현재 device 만)', updated: '기기 신규 측정 시' },
  'device.vdot.updated_at': {
    description: 'VDOT 마지막 갱신 시각 — device 블록이 언제 마지막으로 바뀌었는지 가늠하는 단서',
    updated: '기기 신규 측정 시',
  },

  // ── custom ──
  'custom.states.coaching': {
    description:
      '백엔드 룰 엔진의 준비도 판정. GREEN / AMBER / RED = 위험도, NO_DATA = 판단할 데이터 없음, ANALYZING = 계산 중, NEEDS_CONNECTION = 기기 미연결. 프롬프트는 AMBER/RED 보다 덜 조심스러운 판정을 내리지 말라고 지시.',
    updated: DAILY_SYNC,
  },
  'custom.states.recovery': {
    description:
      '회복 상태 라벨 (NORMAL / LEARNING / NO_DATA / NEEDS_CONNECTION). 참고용 — 단독으로 판정을 정하지 않음.',
    updated: DAILY_SYNC,
  },
  'custom.states.vitality': {
    description: '활력 라벨 (HIGH / MID / ANALYZING / NO_DATA / NEEDS_CONNECTION). 참고용.',
    updated: DAILY_SYNC,
  },
  'custom.states.training_load': {
    description:
      '훈련 부하 라벨 (LOW / STABLE / DANGER / ANALYZING / NO_DATA / NEEDS_CONNECTION). 아래 custom.training_load 숫자 블록과 이름이 같아 혼동 주의.',
    updated: DAILY_SYNC,
  },
  'custom.training_load.atl': { description: 'ATL — 7일 EWMA "피로". 그날 새벽 기준.', updated: DAILY_SYNC },
  'custom.training_load.ctl': { description: 'CTL — 42일 EWMA "체력". 그날 새벽 기준.', updated: DAILY_SYNC },
  'custom.training_load.tsb': {
    description:
      'TSB = CTL − ATL. 쉬는 날 피로가 빠지는 게 반영된 그날 값 — 전문가가 보는 DB 스냅샷과 같은 계산. 프롬프트에는 이 블록 설명이 없어 모델이 잘 인용하지 않는다.',
    updated: DAILY_SYNC,
  },
  'custom.training_load.acwr': {
    description: 'ACWR (그날 새벽 기준). 최근 7일 러닝이 없으면 0.',
    updated: DAILY_SYNC,
  },
  'custom.last_workout.trimp': { description: '마지막 운동 TRIMP', updated: DAILY_SYNC },
  'custom.last_workout.elapsed_sec': {
    description: '마지막 운동 이후 경과 시간 (초). 운동 시간이 아니라 지금까지 지난 시간.',
    updated: DAILY_SYNC,
  },
  'custom.recent_runs': {
    description:
      '최근 7일 러닝 목록 ({date, trimp, avg_hr, distance_km, duration_sec, avg_pace_sec_per_km}). 프롬프트 필드 설명(duration_min, avg_pace_min_km)과 단위가 다르다.',
    updated: DAILY_SYNC,
  },
  'custom.vitals.rhr_z': {
    description: '안정시 심박 z-점수 (본인 기준선 대비). 높을수록 나쁨 — ≥1.5 회복 부족/질병 의심.',
    updated: DAILY_SYNC,
  },
  'custom.vitals.hrv_z': {
    description: 'HRV z-점수 (본인 기준선 대비). 낮을수록 나쁨 — ≤−1.5 회복 부족.',
    updated: DAILY_SYNC,
  },
  'custom.profile.age': { description: '나이', updated: DAILY_SYNC },
  'custom.profile.gender': { description: '성별', updated: DAILY_SYNC },
  'custom.profile.height_cm': { description: '키 (cm)', updated: DAILY_SYNC },
  'custom.profile.weight_kg': { description: '체중 (kg)', updated: DAILY_SYNC },
  'custom.medical_conditions': {
    description: '병력 자유 텍스트. 대부분 [""] (비어 있음).',
    updated: DAILY_SYNC,
  },

  // ── AI result ──
  status: { description: '처리 상태 (completed / failed / processing / pending)', updated: RESULT },
  risk_flag: {
    description: 'green = 달려도 됨, yellow = 대체 운동 권장, red = 휴식 권장 (급성 신호만)',
    updated: RESULT,
  },
  message: { description: '사용자에게 보여주는 한 줄 메시지 (user.language)', updated: RESULT },
  reason: { description: '판정 근거 한 문장. 인용한 수치가 어느 컬럼 값인지 대조해 볼 것.', updated: RESULT },
  suspected_injury: { description: '의심 부상. 근거가 구체적일 때만, 아니면 비어 있음.', updated: RESULT },
  'daily_guide.type': { description: 'easy_run (green) / alt_exercise (yellow) / rest (red)', updated: RESULT },
  'daily_guide.description': {
    description: '오늘의 가이드 — 프롬프트 메뉴에서 하나를 골라 번역한 문장',
    updated: RESULT,
  },
  error_code: { description: '실패 사유 (TIMEOUT, LLM_ERROR 등). 성공이면 비어 있음.', updated: RESULT },
};

export const columnDoc = (key: string): ColumnDoc | undefined => COLUMN_DOCS[key];
